import type { InfiniteData } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { MutationFeedback } from '@/integrations/tanstack-query/types'
import { apiClient } from '@/lib/api-client'

import type {
  GetNotificationStatsResponse,
  ListNotificationsParams,
  ListNotificationsResponse,
} from './notifications.types'

const markNotificationAsRead = async (
  notificationId: string,
): Promise<void> => {
  await apiClient.post(`/v1/platform/notifications/${notificationId}`)
}

type NotificationsInfiniteData = InfiniteData<ListNotificationsResponse>

export const useMarkNotificationAsRead = (feedback?: MutationFeedback) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) =>
      markNotificationAsRead(notificationId),
    // Optimistic: drop the row from unread-filtered lists (flip the flag in
    // the others) and adjust the stats so the row and the sidebar badge react
    // before the server answers.
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] })
      await queryClient.cancelQueries({ queryKey: ['notification-stats'] })

      const previousLists =
        queryClient.getQueriesData<NotificationsInfiniteData>({
          queryKey: ['notifications', 'infinite'],
        })
      const previousStats =
        queryClient.getQueriesData<GetNotificationStatsResponse>({
          queryKey: ['notification-stats'],
        })

      const wasUnread = previousLists.some(([, data]) =>
        data?.pages.some((page) =>
          (page.items ?? []).some(
            (item) => item.id === notificationId && !item.markedAsRead,
          ),
        ),
      )

      for (const [queryKey, data] of previousLists) {
        if (!data?.pages) continue

        const params = queryKey[2] as ListNotificationsParams | undefined
        const filtersUnread = params?.filterByMarkedAsRead === false

        const pages = data.pages.map((page) => {
          const items = page.items ?? []
          const target = items.find((item) => item.id === notificationId)
          if (!target) return page

          if (filtersUnread) {
            return {
              ...page,
              items: items.filter((item) => item.id !== notificationId),
              pagination: page.pagination
                ? {
                    ...page.pagination,
                    totalItems: Math.max(0, page.pagination.totalItems - 1),
                  }
                : page.pagination,
            }
          }

          if (target.markedAsRead) return page
          return {
            ...page,
            items: items.map((item) =>
              item.id === notificationId
                ? { ...item, markedAsRead: true }
                : item,
            ),
          }
        })

        queryClient.setQueryData<NotificationsInfiniteData>(queryKey, {
          ...data,
          pages,
        })
      }

      if (wasUnread) {
        queryClient.setQueriesData<GetNotificationStatsResponse>(
          { queryKey: ['notification-stats'] },
          (old) =>
            old
              ? {
                  ...old,
                  read: old.read + 1,
                  nonRead: Math.max(0, old.nonRead - 1),
                }
              : old,
        )
      }

      return { previousLists, previousStats }
    },
    onError: (_error, _notificationId, context) => {
      context?.previousLists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context?.previousStats.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notification-stats'] })
    },
    meta: {
      ...feedback,
    },
  })
}
